import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';

interface FixedArrCredit {
  id: number;
  status: string;
  initial_amount_dollars: number;
  remaining_amount_dollars: number;
  utilization_percentage: number;
  margin_percentage: number;
  term_start_date: string;
  term_end_date: string;
  days_left: number;
  is_expired: boolean;
  created_by: string;
  is_archived: boolean;
}

interface BrandProfile {
  id: number;
  name: string;
  accountName?: string;
  currentContentStrategyId?: number;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
  a { color: #00a6c2; text-decoration: none; font-size: 14px; &:hover { text-decoration: underline; } }
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const AddButton = styled(Link)`
  padding: 8px 16px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  text-decoration: none;
  &:hover { background: #0094ab; color: white; }
`;

const FilterRow = styled.div`
  background: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

const Checkbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
  input { cursor: pointer; }
`;

const InfoMessage = styled.div`
  background: #e8f4f8;
  border: 1px solid #b8dce8;
  border-radius: 4px;
  padding: 16px 20px;
  color: #2980b9;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const StatusBadge = styled.span<{ $status: string; $archived?: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  ${({ $status, $archived }) => {
    if ($archived) return 'background: #e5e7eb; color: #6b7280;';
    if ($status === 'active') return 'background: #d1fae5; color: #065f46;';
    if ($status === 'expired') return 'background: #fee2e2; color: #991b1b;';
    return 'background: #fef3c7; color: #92400e;';
  }}
`;

const DaysLeft = styled.span<{ $warning?: boolean; $expired?: boolean }>`
  ${({ $warning, $expired }) => {
    if ($expired) return 'color: #dc2626;';
    if ($warning) return 'color: #d97706;';
    return '';
  }}
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 4px;
  &:hover { background: #e5e7eb; }
`;

const DeleteButton = styled(ActionButton)`
  background: #fee2e2;
  border-color: #fecaca;
  color: #991b1b;
  &:hover { background: #fecaca; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ArchivedRow = styled.tr`
  opacity: 0.6;
`;

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

function AdminPublicationFixedArrCreditsPage() {
  const { id } = useParams<{ id: string }>();
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  const [credits, setCredits] = useState<FixedArrCredit[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/admin2/publications/${id}/fixed_arr_credits.json`, {
        params: { show_archived: showArchived }
      });
      setBrandProfile(res.data.brand_profile);
      setCredits(res.data.fixed_arr_credits || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id, showArchived]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleArchive = async (farrId: number, hasBalance: boolean) => {
    const message = hasBalance 
      ? 'This will write off the remaining balance, mark as expired, and archive this credit. Continue?'
      : 'Archive this exhausted FARR credit? It will be hidden from this list.';
    
    if (!window.confirm(message)) return;

    try {
      await api.delete(`/admin2/publications/${id}/fixed_arr_credits/${farrId}.json`);
      toast.success('Fixed ARR credit archived successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to archive Fixed ARR credit');
    }
  };

  const pubDisplayName = brandProfile 
    ? (brandProfile.accountName ? `${brandProfile.accountName} - ${brandProfile.name}` : brandProfile.name) 
    : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Publications" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Publications" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Fixed ARR Credits' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{brandProfile?.name}</Link>
        <span>›</span>
        <span>Fixed arr credits</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Edit {brandProfile?.name}</PageTitle>
        <HeaderActions>
          <a href={`/admin2/publications/${id}/talent_requests`}>Manage talent requests</a>
          {brandProfile?.currentContentStrategyId && (
            <Link to={`/admin2/content_strategies/${brandProfile.currentContentStrategyId}/edit`}>
              Manage content strategy
            </Link>
          )}
        </HeaderActions>
      </PageHeader>

      <TabNav>
        <TabLink to={`/admin2/publications/${id}/edit`}>Publication info</TabLink>
        <TabLink to={`/admin2/publications/${id}/users`}>Users</TabLink>
        <TabLink to={`/admin2/publications/${id}/transactions`}>Transactions</TabLink>
        <TabLink to={`/admin2/publications/${id}/fixed_arr_credits`} $active>Fixed ARR Credits</TabLink>
        <TabLink to={`/admin2/publications/${id}/bulk_story_tagging`}>Bulk Story Tagging</TabLink>
        <TabLink to={`/admin2/publications/${id}/talent_requests`}>Talent Requests</TabLink>
      </TabNav>

      <Section>
        <SectionHeader>
          <SectionTitle>Fixed ARR Credits</SectionTitle>
          <AddButton to={`/admin2/publications/${id}/fixed_arr_credits/new`}>Add Fixed ARR Credit</AddButton>
        </SectionHeader>

        <FilterRow>
          <Checkbox>
            <input 
              type="checkbox" 
              checked={showArchived} 
              onChange={(e) => setShowArchived(e.target.checked)} 
            />
            Show Archived
          </Checkbox>
        </FilterRow>

        {credits.length === 0 ? (
          <InfoMessage>
            <span>ℹ️</span>
            No Fixed ARR credits have been created for this publication yet.
          </InfoMessage>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Status</Th>
                <Th>Initial Amount</Th>
                <Th>Remaining Amount</Th>
                <Th>Utilization</Th>
                <Th>Margin %</Th>
                <Th>Term Start</Th>
                <Th>Term End</Th>
                <Th>Days Left</Th>
                <Th>Created By</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {credits.map((farr) => {
                const Row = farr.is_archived ? ArchivedRow : 'tr';
                return (
                  <Row key={farr.id}>
                    <Td>
                      <StatusBadge $status={farr.status} $archived={farr.is_archived}>
                        {farr.is_archived ? 'Archived' : farr.status.charAt(0).toUpperCase() + farr.status.slice(1)}
                      </StatusBadge>
                    </Td>
                    <Td>{formatCurrency(farr.initial_amount_dollars)}</Td>
                    <Td>{formatCurrency(farr.remaining_amount_dollars)}</Td>
                    <Td>{farr.utilization_percentage}%</Td>
                    <Td>{farr.margin_percentage}%</Td>
                    <Td>{formatDate(farr.term_start_date)}</Td>
                    <Td>{formatDate(farr.term_end_date)}</Td>
                    <Td>
                      <DaysLeft $expired={farr.days_left < 0} $warning={farr.days_left <= 30 && farr.days_left >= 0}>
                        {farr.days_left < 0 ? 'Expired' : `${farr.days_left} days`}
                      </DaysLeft>
                    </Td>
                    <Td>{farr.created_by}</Td>
                    <Td>
                      {farr.is_archived ? (
                        <span style={{ color: '#9ca3af' }}>(read-only)</span>
                      ) : (
                        <>
                          <ActionButton as={Link} to={`/admin2/publications/${id}/fixed_arr_credits/${farr.id}/edit`}>
                            Edit
                          </ActionButton>
                          {farr.status === 'active' && (
                            <DeleteButton onClick={() => handleArchive(farr.id, farr.remaining_amount_dollars > 0)}>
                              {farr.remaining_amount_dollars > 0 ? 'Expire & Write Off' : 'Archive'}
                            </DeleteButton>
                          )}
                        </>
                      )}
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </Table>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminPublicationFixedArrCreditsPage;
